// src/pages/ReportBuilderPage.js
// Admin page for creating and running custom saved reports.

import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../scripts/api';
import Modal from '../components/Modal';
import {
  BarChart2, Plus, Trash2, Edit2, Play, X, ChevronUp, ChevronDown,
  Download, FileText, Printer,
} from 'lucide-react';

// ── Cell formatting ──────────────────────────────────────────────────────────
// PG returns DATE columns as Date objects → res.json() serialises them as full
// ISO timestamps ("2026-06-15T00:00:00.000Z"), which blow out date columns to
// 2 lines. Trim to YYYY-MM-DD whenever a value looks date-like.
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}/;

function formatCellValue(col, raw) {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  if (typeof raw === 'string' && ISO_DATETIME_RE.test(raw)) return raw.slice(0, 10);
  return String(raw);
}

// Column-width / wrapping hints for the printable table.
//   compact  — dates, status chips, short codes (nowrap)
//   wide     — notes/comments fields (allow wrapping, wider min-width)
//   default  — everything else
function colHint(col) {
  const k = String(col || '').toLowerCase();
  if (
    k === 'status' || k === 'color_code' || k.endsWith('_date') ||
    k.endsWith('_eta') || k.startsWith('arrive_') || k === 'to_customer' ||
    k.startsWith('ready_at_') || k.endsWith('_at') || k === 'rowo' ||
    k === 'stock_po' || k === 'tft_po'
  ) return 'compact';
  if (k.includes('notes') || k.includes('comments') || k.includes('description')) return 'wide';
  return 'default';
}

// ── CSV / XLS helpers ────────────────────────────────────────────────────────
function buildCsv(labels, columns, rows) {
  const q = v => {
    const s = String(v ?? '');
    return (s.includes(',') || s.includes('"') || s.includes('\n'))
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    labels.map(q).join(','),
    ...rows.map(r => columns.map(c => q(formatCellValue(c, r[c]))).join(',')),
  ].join('\n');
}

function buildXls(label, labels, columns, rows) {
  const th = labels.map(h => `<th><b>${h}</b></th>`).join('');
  const tr = rows.map(r =>
    '<tr>' + columns.map(c => `<td>${formatCellValue(c, r[c]) || ''}</td>`).join('') + '</tr>'
  ).join('');
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="utf-8"><title>${label}</title></head>
<body><table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`;
}

function downloadBlob(content, filename, mime) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([content], { type: mime }));
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

const ESC_HTML = (s) => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c])
);

function buildPrintHtml(data) {
  const { label, description, columns, labels, rows, generated_at } = data;
  const dateStr = new Date(generated_at).toLocaleString();

  const headerCells = columns.map((c, i) => {
    const cls = colHint(c); // compact | wide | default
    return `<th class="col-${cls}">${ESC_HTML(labels[i] ?? c)}</th>`;
  }).join('');

  const bodyRows = rows.map(r => {
    const tds = columns.map(c => {
      const cls = colHint(c);
      const v = r[c];
      if (c === 'status' && v) {
        const s = STATUS_COLORS[v];
        const style = s
          ? `background:${s.bg};color:${s.color}`
          : 'background:#e2e8f0;color:#374151';
        return `<td class="col-${cls}"><span class="chip" style="${style}">${ESC_HTML(v)}</span></td>`;
      }
      return `<td class="col-${cls}">${ESC_HTML(formatCellValue(c, v))}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><title>${ESC_HTML(label)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; color: #0f172a; padding: 24px; }
  h1 { font-size: 18px; font-weight: 800; margin-bottom: 4px; }
  .meta { font-size: 11px; color: #64748b; margin-bottom: 4px; }
  .desc { font-size: 12px; color: #475569; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; table-layout: auto; }
  thead tr { background: #f1f5f9; }
  th { padding: 8px 10px; text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
  td { padding: 7px 10px; font-size: 11px; color: #1e293b; border-bottom: 1px solid #f1f5f9; vertical-align: top; word-break: break-word; }
  tbody tr:last-child td { border-bottom: none; }
  td:empty::after { content: '—'; color: #cbd5e1; }
  .chip { display: inline-block; padding: 1px 7px; border-radius: 99px; font-size: 9px; font-weight: 700; text-transform: uppercase; white-space: nowrap; }
  /* Per-column-kind sizing — keeps dates / status compact and lets notes flex */
  td.col-compact, th.col-compact { white-space: nowrap; width: 1%; }
  td.col-wide,    th.col-wide    { min-width: 180px; }
  .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #0f172a; color: #fff; padding: 10px 20px; display: flex; gap: 10px; align-items: center; z-index: 99; }
  .toolbar h2 { font-size: 14px; font-weight: 700; flex: 1; }
  .toolbar button { padding: 6px 12px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; font-size: 11px; }
  .toolbar .print { background: #3b82f6; color: #fff; }
  .toolbar .close { background: #475569; color: #fff; }
  .body-wrap { padding-top: 56px; }
  @media print {
    .toolbar { display: none; }
    .body-wrap { padding-top: 0; }
    body { padding: 12px; }
  }
  @page { size: landscape; margin: 12mm; }
</style></head>
<body>
  <div class="toolbar">
    <h2>${ESC_HTML(label)}</h2>
    <button class="print" onclick="window.print()">⎙ Print / Save as PDF</button>
    <button class="close" onclick="window.close()">Close</button>
  </div>
  <div class="body-wrap">
    <h1>${ESC_HTML(label)}</h1>
    ${description ? `<div class="desc">${ESC_HTML(description)}</div>` : ''}
    <div class="meta" style="margin-bottom:16px;">${rows.length} rows · Generated ${ESC_HTML(dateStr)}</div>
    <table>
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </div>
  <script>setTimeout(() => window.print(), 250);</script>
</body></html>`;
}

function printReport(data) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Pop-up blocked. Allow pop-ups for this site to print reports.');
    return;
  }
  w.document.open();
  w.document.write(buildPrintHtml(data));
  w.document.close();
}

const STATUS_COLORS = {
  'Unassigned':               { bg: '#FFFFFF', color: '#000000' },
  'Sold':                     { bg: '#C757FF', color: '#ffffff' },
  'Rented Out':               { bg: '#FFD966', color: '#000000' },
  'In Stock - Calgary':       { bg: '#00B050', color: '#ffffff' },
  'In Transit to Calgary':    { bg: '#92D050', color: '#000000' },
  'In Transit to Customer':   { bg: '#FF8A65', color: '#ffffff' },
  'Alert':                    { bg: '#FF0000', color: '#ffffff' },
  'To Order':                 { bg: '#FFCCFF', color: '#000000' },
  'On Order':                 { bg: '#FFCCFF', color: '#000000' },
  'Stock & PDI - In Calgary': { bg: '#92D050', color: '#000000' },
  'Demo':                     { bg: '#A78BFA', color: '#ffffff' },
  'Chassis Allocation Slot':  { bg: '#94A3B8', color: '#ffffff' },
  'Ready at Factory':         { bg: '#FBBF24', color: '#000000' },
  'In Transit - Broke Down':  { bg: '#DC2626', color: '#ffffff' },
};

function StatusChip({ value }) {
  if (!value) return null;
  const s = STATUS_COLORS[value];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap',
      background: s?.bg ?? '#e2e8f0', color: s?.color ?? '#374151',
    }}>
      {value}
    </span>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function ReportBuilderPage() {
  const [reports, setReports]     = useState([]);
  const [columnMeta, setColMeta]  = useState({});
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState('');
  const [editing, setEditing]     = useState(null);
  const [preview, setPreview]     = useState(null);
  const [running, setRunning]     = useState(null); // id being fetched

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [rRes, cRes] = await Promise.all([
        api.get('/saved-reports'),
        api.get('/saved-reports/columns'),
      ]);
      setReports(rRes.data || []);
      setColMeta(cRes.data || {});
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to load reports');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.delete(`/saved-reports/${id}`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.error || 'Delete failed'); }
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/saved-reports/${id}`);
      setEditing(data);
    } catch (e) { setMsg(e?.response?.data?.error || 'Load failed'); }
  };

  const runReport = async (id, format) => {
    setRunning(id);
    try {
      const { data } = await api.get(`/saved-reports/${id}/run`);
      const slug = data.label.toLowerCase().replace(/\s+/g, '_');
      const date = new Date(data.generated_at).toISOString().slice(0, 10);

      if (format === 'preview') {
        setPreview(data);
      } else if (format === 'csv') {
        downloadBlob(buildCsv(data.labels, data.columns, data.rows), `${slug}_${date}.csv`, 'text/csv;charset=utf-8;');
      } else if (format === 'xls') {
        downloadBlob(buildXls(data.label, data.labels, data.columns, data.rows), `${slug}_${date}.xls`, 'application/vnd.ms-excel');
      } else if (format === 'pdf') {
        printReport(data);
      }
    } catch (e) { setMsg(e?.response?.data?.error || 'Run failed'); }
    finally { setRunning(null); }
  };

  const newBlank = () => setEditing({
    name: '', description: '',
    definition: { table: 'all_inventory', columns: [], filters: [], sort: [] },
  });

  return (
    <div className="container">
      {/* Header */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <BarChart2 size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Report Builder
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Create custom reports by selecting columns, filters, and sort order.
          </div>
        </div>
        <button className="btn" onClick={newBlank}>
          <Plus size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          New Report
        </button>
      </div>

      {msg && <div className="card" style={{ marginTop: 10, color: '#dc2626' }}>{msg}</div>}

      {/* Saved reports list */}
      <div className="card" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Saved Reports</div>
        {busy && <div className="muted">Loading…</div>}
        {!busy && reports.length === 0 && (
          <div className="muted">No saved reports yet. Click "New Report" to create one.</div>
        )}
        <div style={{ display: 'grid', gap: 10 }}>
          {reports.map(r => (
            <div key={r.id} className="card" style={{ padding: 14, background: 'rgba(255,255,255,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{r.name}</div>
                {r.description && <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{r.description}</div>}
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Updated {new Date(r.updated_at).toLocaleDateString()}
                </div>
              </div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <button
                  className="btn-outline"
                  disabled={running === r.id}
                  onClick={() => runReport(r.id, 'preview')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Play size={12} /> Preview
                </button>
                <button className="btn-outline" disabled={running === r.id} onClick={() => runReport(r.id, 'csv')}>↓ CSV</button>
                <button className="btn-outline" disabled={running === r.id} onClick={() => runReport(r.id, 'xls')}>↓ XLS</button>
                <button className="btn-outline" disabled={running === r.id} onClick={() => runReport(r.id, 'pdf')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Printer size={12} /> PDF
                </button>
                <button className="btn-outline" onClick={() => openEdit(r.id)}>
                  <Edit2 size={13} style={{ marginRight: 4, verticalAlign: -2 }} />Edit
                </button>
                <button className="btn-outline" onClick={() => deleteReport(r.id)} style={{ color: '#dc2626' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inline editor */}
      {editing && (
        <ReportEditor
          report={editing}
          columnMeta={columnMeta}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Preview modal */}
      {preview && (
        <PreviewModal
          data={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

// ── Operator definitions ─────────────────────────────────────────────────────
const OPERATORS = [
  { value: '=',           label: 'equals'          },
  { value: '!=',          label: 'does not equal'  },
  { value: 'LIKE',        label: 'contains'        },
  { value: 'NOT LIKE',    label: "doesn't contain" },
  { value: 'IS NULL',     label: 'is empty'        },
  { value: 'IS NOT NULL', label: 'is not empty'    },
  { value: 'IN',          label: 'is one of'       },
  { value: 'NOT IN',      label: 'is none of'      },
  { value: '>',           label: 'greater than'    },
  { value: '<',           label: 'less than'       },
  { value: '>=',          label: '>= to'           },
  { value: '<=',          label: '<= to'           },
];
const NO_VALUE_OPS = new Set(['IS NULL', 'IS NOT NULL']);

// ── Report editor ────────────────────────────────────────────────────────────
function ReportEditor({ report, columnMeta, onSaved, onCancel }) {
  const isNew = !report?.id;

  const [name, setName]           = useState(report?.name || '');
  const [description, setDesc]    = useState(report?.description || '');
  const [table, setTable]         = useState(report?.definition?.table || 'all_inventory');
  const [selectedCols, setCols]   = useState(
    Array.isArray(report?.definition?.columns) ? report.definition.columns : []
  );
  const [filters, setFilters]     = useState(
    Array.isArray(report?.definition?.filters) ? report.definition.filters : []
  );
  const [sort, setSort]           = useState(
    Array.isArray(report?.definition?.sort) ? report.definition.sort : []
  );
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  const availableCols = columnMeta[table] || [];
  const selectedKeys  = new Set(selectedCols.map(c => c.col));

  const changeTable = (t) => { setTable(t); setCols([]); setFilters([]); setSort([]); };

  const toggleCol = (meta) => {
    if (selectedKeys.has(meta.col)) {
      setCols(prev => prev.filter(c => c.col !== meta.col));
    } else {
      setCols(prev => [...prev, { col: meta.col, label: meta.label }]);
    }
  };

  const moveCol = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= selectedCols.length) return;
    const n = [...selectedCols]; [n[idx], n[t]] = [n[t], n[idx]]; setCols(n);
  };

  const updateColLabel = (idx, label) => {
    const n = [...selectedCols]; n[idx] = { ...n[idx], label }; setCols(n);
  };

  const addFilter = () => {
    const first = availableCols[0];
    if (!first) return;
    setFilters(prev => [...prev, { col: first.col, op: '=', val: '' }]);
  };

  const updateFilter = (idx, field, value) => {
    const n = [...filters]; n[idx] = { ...n[idx], [field]: value }; setFilters(n);
  };

  const addSort = () => {
    const first = availableCols[0];
    if (!first) return;
    setSort(prev => [...prev, { col: first.col, dir: 'ASC' }]);
  };

  const updateSort = (idx, field, value) => {
    const n = [...sort]; n[idx] = { ...n[idx], [field]: value }; setSort(n);
  };

  const save = async () => {
    setErr(''); setBusy(true);
    const definition = { table, columns: selectedCols, filters, sort };
    try {
      if (isNew) {
        await api.post('/saved-reports', { name: name.trim(), description, definition });
      } else {
        await api.put(`/saved-reports/${report.id}`, { name: name.trim(), description, definition });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 900, fontSize: 16 }}>
          {isNew ? 'New Report' : `Edit: ${report.name}`}
        </div>
        <button className="btn-outline" onClick={onCancel}><X size={14} /></button>
      </div>

      {err && <div className="card" style={{ marginBottom: 12, color: '#dc2626' }}>{err}</div>}

      {/* Name / description / table */}
      <EdSection title="Basic Info">
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Report Name</div>
            <input className="input" style={{ width: '100%' }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Prairie Disposal Status Update" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Description (optional)</div>
            <input className="input" style={{ width: '100%' }} value={description} onChange={e => setDesc(e.target.value)} placeholder="What this report shows" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Source Table</div>
            <select className="input" value={table} onChange={e => changeTable(e.target.value)}>
              <option value="all_inventory">All Inventory</option>
              <option value="moves">Moves</option>
            </select>
          </div>
        </div>
      </EdSection>

      {/* Column picker */}
      <EdSection title="Columns" hint="Check columns to include. Use arrows to reorder; edit the label shown in the report header.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '4px 16px', padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid var(--card-border)', marginBottom: 10 }}>
          {availableCols.map(col => (
            <label key={col.col} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, padding: '3px 0', userSelect: 'none' }}>
              <input type="checkbox" checked={selectedKeys.has(col.col)} onChange={() => toggleCol(col)} />
              <span>{col.label}</span>
            </label>
          ))}
        </div>

        {selectedCols.length > 0 && (
          <>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Order &amp; Display Labels</div>
            <div style={{ display: 'grid', gap: 5 }}>
              {selectedCols.map((c, idx) => (
                <div key={c.col} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <button className="btn-outline" style={{ padding: '2px 5px', minWidth: 26 }} onClick={() => moveCol(idx, -1)} disabled={idx === 0}><ChevronUp size={11} /></button>
                    <button className="btn-outline" style={{ padding: '2px 5px', minWidth: 26 }} onClick={() => moveCol(idx, 1)} disabled={idx === selectedCols.length - 1}><ChevronDown size={11} /></button>
                  </div>
                  <code style={{ fontSize: 11, opacity: 0.55, minWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.col}</code>
                  <input
                    className="input"
                    style={{ flex: 1, fontSize: 12 }}
                    value={c.label}
                    onChange={e => updateColLabel(idx, e.target.value)}
                    placeholder="Display label"
                  />
                  <button className="btn-outline" style={{ color: '#dc2626', padding: '5px 7px' }} onClick={() => setCols(prev => prev.filter((_, i) => i !== idx))}><X size={11} /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </EdSection>

      {/* Filters */}
      <EdSection title="Filters" hint="Only rows matching all conditions will be included. Filter rows with a blank value are ignored.">
        <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
          {filters.map((f, idx) => {
            const needsVal = !NO_VALUE_OPS.has(f.op);
            const blank    = needsVal && (f.val == null || f.val === '');
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <select className="input" style={{ flex: '1 1 140px' }} value={f.col} onChange={e => updateFilter(idx, 'col', e.target.value)}>
                  {availableCols.map(c => <option key={c.col} value={c.col}>{c.label}</option>)}
                </select>
                <select className="input" style={{ flex: '0 0 130px' }} value={f.op} onChange={e => updateFilter(idx, 'op', e.target.value)}>
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {needsVal && (
                  <input
                    className="input"
                    style={{ flex: '1 1 100px', borderColor: blank ? '#f59e0b' : undefined }}
                    value={f.val || ''}
                    onChange={e => updateFilter(idx, 'val', e.target.value)}
                    placeholder={['IN', 'NOT IN'].includes(f.op) ? 'val1, val2, …' : 'value'}
                  />
                )}
                {blank && (
                  <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>ignored (no value)</span>
                )}
                <button className="btn-outline" style={{ color: '#dc2626', padding: '5px 7px' }} onClick={() => setFilters(prev => prev.filter((_, i) => i !== idx))}><X size={13} /></button>
              </div>
            );
          })}
        </div>
        <button className="btn-outline" onClick={addFilter} style={{ fontSize: 12 }}>
          <Plus size={13} style={{ marginRight: 4, verticalAlign: -2 }} />Add Filter
        </button>
      </EdSection>

      {/* Sort */}
      <EdSection title="Sort Order" hint="Rows will be sorted in this order. Leave empty for default DB order.">
        <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
          {sort.map((s, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select className="input" style={{ flex: 1 }} value={s.col} onChange={e => updateSort(idx, 'col', e.target.value)}>
                {availableCols.map(c => <option key={c.col} value={c.col}>{c.label}</option>)}
              </select>
              <select className="input" style={{ width: 100 }} value={s.dir} onChange={e => updateSort(idx, 'dir', e.target.value)}>
                <option value="ASC">A → Z</option>
                <option value="DESC">Z → A</option>
              </select>
              <button className="btn-outline" style={{ color: '#dc2626', padding: '5px 7px' }} onClick={() => setSort(prev => prev.filter((_, i) => i !== idx))}><X size={13} /></button>
            </div>
          ))}
        </div>
        <button className="btn-outline" onClick={addSort} style={{ fontSize: 12 }}>
          <Plus size={13} style={{ marginRight: 4, verticalAlign: -2 }} />Add Sort
        </button>
      </EdSection>

      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
        <button className="btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          className="btn"
          onClick={save}
          disabled={busy || !name.trim() || !selectedCols.length}
        >
          {isNew ? 'Create Report' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function EdSection({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: hint ? 2 : 8 }}>{title}</div>
      {hint && <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}

// ── Preview modal ────────────────────────────────────────────────────────────
function PreviewModal({ data, onClose }) {
  const slug = data.label.toLowerCase().replace(/\s+/g, '_');
  const date = new Date(data.generated_at).toISOString().slice(0, 10);

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{data.label}</h3>
        <button className="btn-outline" onClick={onClose}><X size={14} /></button>
      </div>
      {data.description && <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>{data.description}</div>}
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        {data.rows.length} rows · Generated {new Date(data.generated_at).toLocaleString()}
      </div>

      <div style={{ overflowX: 'auto', maxHeight: 500, border: '1px solid var(--card-border)', borderRadius: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {data.labels.map((h, i) => {
                const hint = colHint(data.columns[i]);
                return (
                  <th
                    key={i}
                    style={{
                      padding: '9px 11px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      color: '#64748b', borderBottom: '2px solid #e2e8f0',
                      whiteSpace: 'nowrap',
                      width: hint === 'compact' ? '1%' : undefined,
                      minWidth: hint === 'wide' ? 180 : undefined,
                    }}
                  >
                    {h}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 200).map((row, ri) => (
              <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9' }}>
                {data.columns.map((col, ci) => {
                  const hint = colHint(col);
                  return (
                    <td
                      key={ci}
                      style={{
                        padding: '8px 11px',
                        verticalAlign: 'top',
                        color: '#1e293b',
                        whiteSpace: hint === 'compact' ? 'nowrap' : undefined,
                        wordBreak: hint === 'compact' ? undefined : 'break-word',
                      }}
                    >
                      {col === 'status'
                        ? <StatusChip value={row[col]} />
                        : formatCellValue(col, row[col])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.rows.length > 200 && (
          <div className="muted" style={{ fontSize: 12, padding: 10, textAlign: 'center' }}>
            Showing first 200 of {data.rows.length} rows. Download CSV/XLS for the full dataset.
          </div>
        )}
      </div>

      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button
          className="btn-outline"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => downloadBlob(buildCsv(data.labels, data.columns, data.rows), `${slug}_${date}.csv`, 'text/csv;charset=utf-8;')}
        >
          <Download size={13} /> CSV
        </button>
        <button
          className="btn-outline"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => downloadBlob(buildXls(data.label, data.labels, data.columns, data.rows), `${slug}_${date}.xls`, 'application/vnd.ms-excel')}
        >
          <FileText size={13} /> XLS
        </button>
        <button
          className="btn-outline"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => printReport(data)}
        >
          <Printer size={13} /> Print / PDF
        </button>
        <button className="btn-outline" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}
